/**
 * `items.get_active_blanket_orders` — the source for the contract picker, LIVE since 2026-09-03.
 *
 * Full measurement of the payload, and the quirks pinned against it, in this module's test.
 * What matters here:
 *
 *  - It returns only genuinely active, valid-dated contracts (5 of the bench's 17), which is what
 *    the name promises and what the earlier `docstatus`-filtered version could not deliver.
 *  - Every order carries its ITEMS INLINE. That is the significant part: selecting contracts used
 *    to cost one `items.get_blanket_order_item` request per contract (~1.25s each) to build the
 *    rate index. The whole index — 420 rates across 5 contracts — now arrives with the list.
 *  - `supplier` and `supplier_name` genuinely differ ("SEA LAND FOODSTUFF TRADG LLC,AJMAN" vs
 *    "SEA LAND FOODSTUFF TRADING LLC"), so the two are kept apart: the first is the Link value
 *    everything is keyed and written on, the second is the only one a purchaser should read.
 */

import { num, str } from './wire'
import type { BlanketOrderDetail } from '../blanketOrders'

export interface WireActiveBlanketOrderItem {
  item_code?: unknown
  item_name?: unknown
  rate?: unknown
  uom?: unknown
  qty?: unknown
}

export interface WireActiveBlanketOrderRow {
  name?: unknown
  supplier?: unknown
  supplier_name?: unknown
  currency?: unknown
  status?: unknown
  valid_from?: unknown
  valid_till?: unknown
  blanket_order_type?: unknown
  notes?: unknown
  items?: WireActiveBlanketOrderItem[]
}

export interface WireActiveBlanketOrders {
  status?: unknown
  total?: unknown
  orders?: WireActiveBlanketOrderRow[]
}

export interface BlanketOrderSummaryShape {
  name: string
  /** The Supplier LINK value — what everything is keyed, grouped and written on. */
  supplier: string
  /** Display only. Never key on this: it differs from `supplier` on real rows. */
  supplier_name: string
  status: string
  /** `YYYY-MM-DD`, or `''`. A contract's remaining life is worth seeing when choosing one. */
  valid_from: string
  valid_till: string
}

const rows = (wire: WireActiveBlanketOrders | undefined): WireActiveBlanketOrderRow[] =>
  Array.isArray(wire?.orders) ? wire.orders : []

export function toBlanketOrderSummaries(wire: WireActiveBlanketOrders | undefined): BlanketOrderSummaryShape[] {
  return rows(wire)
    .map((row) => {
      const supplier = str(row?.supplier)
      return {
        name: str(row?.name),
        supplier,
        // Falls back to the link value so the picker always has something to print — but only as a
        // fallback: where a real name exists it is the one shown.
        supplier_name: str(row?.supplier_name) || supplier,
        // This method returns only active orders, so a row with no status IS active. Defaulting to
        // `''` would make it unselectable in `BlanketOrderBar` (which admits only `Active`) — this
        // adapter quietly discarding contracts the server just vouched for.
        status: str(row?.status) || 'Active',
        valid_from: str(row?.valid_from),
        valid_till: str(row?.valid_till),
      }
    })
    .filter((o) => o.name !== '' && o.supplier !== '')
}

/**
 * The contracted rates, straight off the same response — `order docname → its item list`.
 *
 * Keyed by docname because that is what a selection holds and what `useBlanketOrderItems` looks
 * up. Rows that cannot price a line are dropped here rather than downstream:
 *
 *  - no `item_code`: matches no enquiry line, ever.
 *  - `rate <= 0`: 4 of the 420 live rows. A contract pricing an item at nothing is a MISSING
 *    price, not a free item, and routing a line to `contract` on it would quote the customer zero.
 *
 * `uom` is null on all 420 rows and passes through as null — `BlanketRate.uom` already documents
 * that. `qty` is 1.0 on all 420: a placeholder, not a contracted volume, so nothing promotes it.
 */
export function toBlanketOrderDetails(wire: WireActiveBlanketOrders | undefined): Map<string, BlanketOrderDetail> {
  const out = new Map<string, BlanketOrderDetail>()

  for (const row of rows(wire)) {
    const name = str(row?.name)
    const supplier = str(row?.supplier)
    if (name === '' || supplier === '') continue

    const items = (Array.isArray(row?.items) ? row.items : [])
      .map((item) => ({
        item_code: str(item?.item_code),
        item_name: str(item?.item_name),
        rate: num(item?.rate),
        uom: item?.uom === null || item?.uom === undefined ? null : str(item.uom),
        qty: num(item?.qty),
      }))
      .filter((item) => item.item_code !== '' && item.rate > 0)

    out.set(name, { name, supplier, currency: str(row?.currency) || null, items })
  }

  return out
}
